// Per-bone capsule colliders for the cat. Each kinematic-position-based
// RigidBody tracks a key skeleton bone (Spine2 / Spine / Head); capsules
// reposition automatically when the cat sits, lies, stretches, or
// transitions, so contact pairs match the visible mesh through every
// animation. Pulled out of cat.ts to keep that file focused on the AI
// loop.

import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import type { PhysicsHandle } from "./physics";

// Capsule axis is Rapier's local +Y. Bones in the Indie Cat rig are
// along their local +Y in the rest pose (Blender / FBX convention) —
// empirically verified: a +0.4 localYOffset on the Head capsule shifts
// it +0.4 in world X, which is the cat's forward direction. So a
// capsule attached at a bone's pose with the bone's rotation runs
// along the bone's natural body-line direction.
//
// GLTFLoader name-mangling: three.js's GLTFLoader strips dots from
// bone names — "Spine.1" in the FBX/GLB becomes "Spine1" in the
// SkinnedMesh skeleton. Bone names below match the runtime spelling.
//
// The original cat physics used three giant overlapping pills sized
// for cup-shoving (body radius 1.4, head 0.8). They worked but bore no
// resemblance to the visible cat — the debug overlay looked like a
// gas cloud. The current set is anatomical: one capsule per major body
// segment (torso, hindquarters, head/neck, four upper legs, four
// lower legs). Cup-shoving still works because each lower-leg capsule
// reaches down to roughly paw level (Forearm/Shin's halfHeight covers
// elbow→paw and knee→paw respectively), so cups in the cat's footfall
// path catch on a leg instead of on a body-sized envelope.
//
// Sizes are tuned to the Indie Cat rig at SCALE=7.5:
//   torso girth ~0.8 → radius 0.4
//   leg girth   ~0.2-0.3 → radius 0.10-0.17
// Bone segment lengths measured at runtime (see scripts/debug/list-cat-
// bones.mjs) are baked into the halfHeight + offset so the capsule
// centers on the segment midpoint.
type BoneCapsuleConfig = {
  boneName: string;
  radius: number;
  halfHeight: number;
  /** Offset in the bone's local frame applied AFTER the bone's
   * rotation. For limb bones, local +Y points DOWN the bone toward the
   * next joint, so a positive y shifts the capsule along the bone
   * (into its midpoint, given the bone's anchor is at the proximal
   * end). */
  localOffset?: { x?: number; y?: number; z?: number };
};

const BONE_CAPSULES: BoneCapsuleConfig[] = [
  // Torso (Spine2 anchor) — horizontal capsule along the body's forward
  // axis covering Spine → Spine3 (~1.9 units of spine, slight curve).
  // Radius 0.33 fits the cat's torso girth without bulging above the
  // visible back surface (was 0.4, dropped after debug overlay showed
  // the capsule sticking up past the topline).
  {
    boneName: "Spine2",
    radius: 0.33,
    halfHeight: 0.5,
    localOffset: { y: -0.3 },
  },
  // (Spine hindquarters sphere removed — was a 0.35 ball at the hip,
  // visually conspicuous near the tail base. The Spine2 rear cap +
  // ThighL/R capsules cover the hip area without it.)
  // Head + neck (Neck1 anchor).
  {
    boneName: "Neck1",
    radius: 0.3,
    halfHeight: 0.25,
    localOffset: { y: 0.25 },
  },

  // Front upper legs — shoulder to elbow (~0.83 units).
  {
    boneName: "UpperArmL",
    radius: 0.13,
    halfHeight: 0.35,
    localOffset: { y: 0.4 },
  },
  {
    boneName: "UpperArmR",
    radius: 0.13,
    halfHeight: 0.35,
    localOffset: { y: 0.4 },
  },
  // Front lower legs — elbow to paw (~0.99 units; covers Forearm + Pastern + Hand).
  {
    boneName: "ForearmL",
    radius: 0.11,
    halfHeight: 0.5,
    localOffset: { y: 0.5 },
  },
  {
    boneName: "ForearmR",
    radius: 0.11,
    halfHeight: 0.5,
    localOffset: { y: 0.5 },
  },

  // Back upper legs — hip to knee (~0.69 units).
  {
    boneName: "ThighL",
    radius: 0.17,
    halfHeight: 0.3,
    localOffset: { y: 0.35 },
  },
  {
    boneName: "ThighR",
    radius: 0.17,
    halfHeight: 0.3,
    localOffset: { y: 0.35 },
  },
  // Back middle legs — knee to hock (Shin segment, ~0.85 units).
  {
    boneName: "ShinL",
    radius: 0.13,
    halfHeight: 0.35,
    localOffset: { y: 0.4 },
  },
  {
    boneName: "ShinR",
    radius: 0.13,
    halfHeight: 0.35,
    localOffset: { y: 0.4 },
  },
  // Back paws — hock to paw (~0.66 units). The cat's back legs bend
  // forward at the hock (digitigrade), so the foot points in a
  // different direction than the shin and won't be covered by the
  // Shin capsule's straight-along-axis extent. A separate capsule
  // anchored at HookL/R follows the foot's direction.
  {
    boneName: "HookL",
    radius: 0.11,
    halfHeight: 0.25,
    localOffset: { y: 0.3 },
  },
  {
    boneName: "HookR",
    radius: 0.11,
    halfHeight: 0.25,
    localOffset: { y: 0.3 },
  },

  // Tail. Eight bones in the chain (tail / tail1…tail7) totaling ~2
  // units, curving downward from the spine to almost the floor in the
  // resting pose. A single capsule anchored at `tail` would cover only
  // the first segment direction, missing everything past tail1's bend.
  // Four capsules anchored at every-other-bone follow the curve well
  // enough that a swung tail can shove cups, while keeping the body
  // count bounded. Radius 0.08 matches the visible tail thickness.
  {
    boneName: "tail",
    radius: 0.09,
    halfHeight: 0.22,
    localOffset: { y: 0.22 },
  },
  {
    boneName: "tail2",
    radius: 0.08,
    halfHeight: 0.3,
    localOffset: { y: 0.3 },
  },
  {
    boneName: "tail4",
    radius: 0.08,
    halfHeight: 0.32,
    localOffset: { y: 0.32 },
  },
  {
    boneName: "tail6",
    radius: 0.07,
    halfHeight: 0.22,
    localOffset: { y: 0.22 },
  },
];

interface BoneCapsuleEntry {
  bone: THREE.Object3D;
  body: RAPIER.RigidBody;
  localOffset: THREE.Vector3;
}

export interface CatBoneCapsules {
  /** Every kinematic body in the cat's collider set. Pass to
   * `physics.setCatBodies` so the fracture detector exempts contacts
   * with any of them. */
  bodies: RAPIER.RigidBody[];
  /** Drive each capsule body from its bone's current world transform.
   * Pass `teleport=true` to use `setTranslation/Rotation` (no sweep)
   * — for initial spawn and visibility toggle, so dynamic bodies in
   * the way aren't shoved. */
  sync: (teleport?: boolean) => void;
  /** Enable or disable every cat body in one call. Used by setVisible
   * to take the cat out of physics during focus mode. */
  setEnabled: (enabled: boolean) => void;
}

/**
 * First SkinnedMesh found under `model`, or null. Loaders sometimes park
 * the skinned mesh as a sibling of the rendered tree, so caller code
 * uses `traverse` rather than walking children.
 */
export function findSkinnedMesh(
  model: THREE.Object3D,
): THREE.SkinnedMesh | null {
  let found: THREE.SkinnedMesh | null = null;
  model.traverse((o) => {
    if (found) return;
    const sm = o as THREE.SkinnedMesh;
    if (sm.isSkinnedMesh) found = sm;
  });
  return found;
}

/**
 * Bone lookup against the canonical `skeleton.bones` list. Use this
 * (not `model.getObjectByName`) — bones can be parented outside the
 * rendered scene tree and the canonical list is what the SkinnedMesh
 * actually animates against.
 *
 * GLTFLoader caveat: bone names with dots get stripped — "Spine.1" in
 * the FBX becomes "Spine1" at runtime. Match the runtime spelling.
 */
export function findBoneByName(
  skinned: THREE.SkinnedMesh,
  name: string,
): THREE.Bone | null {
  return skinned.skeleton.bones.find((b) => b.name === name) ?? null;
}

/**
 * Walk the cat model's skeleton, find the bones we want to attach
 * capsules to, and create one kinematic body per capsule. Returns a
 * handle the caller drives every frame via `sync()`.
 */
export function createBoneCapsules(
  model: THREE.Object3D,
  physics: PhysicsHandle,
): CatBoneCapsules {
  const skinned = findSkinnedMesh(model);
  const skeletonBones = skinned ? skinned.skeleton.bones : [];

  const entries: BoneCapsuleEntry[] = [];
  for (const cfg of BONE_CAPSULES) {
    const bone = skeletonBones.find((b) => b.name === cfg.boneName) ?? null;
    if (!bone) {
      console.warn(`[cupilot] cat bone missing: ${cfg.boneName}`);
      continue;
    }
    const body = physics.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased(),
    );
    physics.world.createCollider(
      RAPIER.ColliderDesc.capsule(cfg.halfHeight, cfg.radius)
        .setFriction(0.55)
        .setRestitution(0.05),
      body,
    );
    entries.push({
      bone,
      body,
      localOffset: new THREE.Vector3(
        cfg.localOffset?.x ?? 0,
        cfg.localOffset?.y ?? 0,
        cfg.localOffset?.z ?? 0,
      ),
    });
  }

  // Reusable scratch — avoid allocating a Vector3/Quaternion per bone
  // per frame in the hot sync loop.
  const tmpPos = new THREE.Vector3();
  const tmpQuat = new THREE.Quaternion();

  function sync(teleport = false): void {
    // Bones live deep under the model; their world matrix depends on
    // `model.matrixWorld`, which only updates when a parent renders OR
    // we ask. Force-compute it.
    model.updateMatrixWorld(true);
    for (const cap of entries) {
      cap.bone.getWorldPosition(tmpPos);
      cap.bone.getWorldQuaternion(tmpQuat);
      // Apply the local offset along the bone's current direction.
      if (cap.localOffset.lengthSq() > 0) {
        const off = cap.localOffset.clone().applyQuaternion(tmpQuat);
        tmpPos.add(off);
      }
      const t = { x: tmpPos.x, y: tmpPos.y, z: tmpPos.z };
      const r = { x: tmpQuat.x, y: tmpQuat.y, z: tmpQuat.z, w: tmpQuat.w };
      if (teleport) {
        cap.body.setTranslation(t, true);
        cap.body.setRotation(r, true);
      } else {
        cap.body.setNextKinematicTranslation(t);
        cap.body.setNextKinematicRotation(r);
      }
    }
  }

  function setEnabled(enabled: boolean): void {
    for (const cap of entries) cap.body.setEnabled(enabled);
  }

  return {
    bodies: entries.map((e) => e.body),
    sync,
    setEnabled,
  };
}
